import { useRef, useState } from 'react'
import { buildingImagesRootGuid } from '@constants'
import { Camera, ContentCopy } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, Tabs, Tab } from '@mui/material'
import { ToggleButton, ToggleButtonGroup } from '@ui/actions'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { DraggablePopup } from '@ui/structure'
import { useControlStore } from '@editorStores'
import { useProjectFiles, useUploadProjectDocumentationFile } from '@editorHooks'

enum ScreenshotTab {
  NEW_IMAGE = 0,
  CURRENT_IMAGE = 1,
}

const ModelScreenshotWidget = () => {
  const screenshotToolElement = useControlStore(state => state.screenshotToolElement)
  const setScreenshotToolElement = useControlStore(state => state.setScreenshotToolElement)

  const { enqueueSnackbar } = useSnackbar()

  const [currentScreenshot, setCurrentScreenshot] = useState<string | null>(null)
  const [tabValue, setTabValue] = useState<ScreenshotTab>(ScreenshotTab.NEW_IMAGE)

  const handleClose = () => {
    setScreenshotToolElement(null)
  }

  const ref = useRef<HTMLElement>(null)

  const captureScreenshot = () => {
    try {
      const canvas = document.querySelector('canvas')

      if (!canvas) {
        return
      }

      const dataURL = canvas.toDataURL('image/png')
      setCurrentScreenshot(dataURL)
    } catch (error) {
      enqueueSnackbar('Fehler beim Aufnehmen des Screenshots', { variant: 'error' })
    }
  }

  const copyScreenshotToClipboard = async () => {
    if (!currentScreenshot) {
      enqueueSnackbar('Kein Screenshot zum Kopieren vorhanden', { variant: 'warning' })
      return
    }

    try {
      // Convert data URL to blob
      const response = await fetch(currentScreenshot)
      const blob = await response.blob()

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ])

      enqueueSnackbar('Screenshot in die Zwischenablage kopiert', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Fehler beim Kopieren in die Zwischenablage', { variant: 'error' })
    }
  }

  const { mutateAsync: uploadScreenshot, isLoading: isUploadingScreenshot } =
    useUploadProjectDocumentationFile(buildingImagesRootGuid)

  const { data: buildingImages, isFetching: isFetchingBuildingImages } =
    useProjectFiles(buildingImagesRootGuid)

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue as ScreenshotTab)
  }

  // Get the latest image from buildingImages
  const latestImage = buildingImages?.files?.sort(
    (a, b) => new Date(b.created || '').getTime() - new Date(a.created || '').getTime(),
  )?.[0]

  return (
    <>
      <ToggleButtonGroup>
        <ToggleButton
          hint="Bild des Modells erstellen"
          onClick={event => {
            if (screenshotToolElement) {
              setScreenshotToolElement(null)
            } else {
              setScreenshotToolElement(event.currentTarget)
            }
          }}
          selected={!!screenshotToolElement}
          Icon={Camera}
          // @ts-ignore
          ref={ref}
        />
      </ToggleButtonGroup>

      <DraggablePopup
        title={
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="screenshot tabs">
            <Tab label="Neues Bild" />
            <Tab label="Aktuelles Bild" />
          </Tabs>
        }
        closePopover={handleClose}
        anchorEl={screenshotToolElement || undefined}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ p: 0, minHeight: '500px' }}>
            {tabValue === ScreenshotTab.NEW_IMAGE && (
              <Box>
                <Box minWidth={500} sx={{ mb: 2 }}>
                  {currentScreenshot && (
                    <img src={currentScreenshot} alt="Screenshot" height={400} />
                  )}
                </Box>
                <Stack direction="row" spacing={2}>
                  <Button onClick={captureScreenshot} variant="contained" color="primary">
                    Neu Aufnehmen
                  </Button>
                  {currentScreenshot && (
                    <>
                      <Button
                        onClick={copyScreenshotToClipboard}
                        variant="outlined"
                        color="primary"
                        startIcon={<ContentCopy />}
                      >
                        Kopieren
                      </Button>
                      <LoadingButton
                        onClick={() => {
                          // Convert data URL to binary data
                          const base64Data = currentScreenshot.split(',')[1]
                          const binaryData = atob(base64Data)
                          const bytes = new Uint8Array(binaryData.length)
                          for (let i = 0; i < binaryData.length; i++) {
                            bytes[i] = binaryData.charCodeAt(i)
                          }

                          const blob = new Blob([bytes], { type: 'image/png' })
                          const file = new File([blob], 'screenshot.png')

                          // Create FileItem object with required properties
                          const fileItem: FileItem & { file: File } = {
                            guid: '', // Will be generated by the server
                            parent_guid: buildingImagesRootGuid,
                            name: 'screenshot.png',
                            hierarchy_position: 0,
                            type: 'file',
                            file: file,
                          }

                          uploadScreenshot(fileItem)
                        }}
                        variant="contained"
                        color="primary"
                        loading={isUploadingScreenshot}
                      >
                        Hochladen
                      </LoadingButton>
                    </>
                  )}
                </Stack>
              </Box>
            )}
            {tabValue === ScreenshotTab.CURRENT_IMAGE && (
              <Box>
                {isFetchingBuildingImages ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 400,
                    }}
                  >
                    <LoadingIndicator />
                  </Box>
                ) : latestImage ? (
                  <Box>
                    <Box minWidth={500} sx={{ mb: 2 }}>
                      <img src={latestImage.url || ''} alt={latestImage.name} height={400} />
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <strong>Erstellt:</strong>{' '}
                      {latestImage.created
                        ? new Date(latestImage.created).toLocaleString()
                        : 'Unbekannt'}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 400,
                    }}
                  >
                    Keine Bilder vorhanden
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DraggablePopup>
    </>
  )
}

export default ModelScreenshotWidget
